import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { QrCode, Download, Plus, Printer, Upload, Users, TrendingUp, ScanLine } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * QRCodesTab - admin/PM UI to:
 *  - View/download the property site QR (used by agents to clock in)
 *  - List all existing unit QRs for a property
 *  - Batch-generate QRs for a custom set of unit numbers (comma / newline separated)
 */
export default function QRCodesTab({ isPropertyManager = false, dataTestId = 'qr-codes-tab', lockedPropertyId = null }) {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(lockedPropertyId || '');
  const [propertyQR, setPropertyQR] = useState(null);
  const [unitQRs, setUnitQRs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [batchInput, setBatchInput] = useState('');
  const [residentsUploading, setResidentsUploading] = useState(false);
  const [residentsCount, setResidentsCount] = useState(0);
  // ── QR adoption pilot panel (CEO Feb 2026 potential-improvement).
  // Scan-through rate powers a "First-week adoption" card so JK can
  // see how the QR pickup verification pilot is landing per property
  // before rolling out to more metros.
  const [adoption, setAdoption] = useState(null);
  const [adoptionWindow, setAdoptionWindow] = useState(7);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/qr/pickups/adoption?days=${adoptionWindow}`,
          { credentials: 'include' },
        );
        if (res.ok) setAdoption(await res.json());
      } catch (_e) { /* noop */ }
    })();
  }, [adoptionWindow]);

  useEffect(() => {
    if (lockedPropertyId) {
      // Skip loading the property list — we already know which one to display.
      setSelectedPropertyId(lockedPropertyId);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/properties`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          // For property managers, only show their own properties
          const scoped = isPropertyManager && user?.user_id
            ? (data || []).filter((p) => p.manager_id === user.user_id)
            : (data || []);
          setProperties(scoped);
          if (scoped.length && !selectedPropertyId) setSelectedPropertyId(scoped[0].property_id);
        }
      } catch (_e) { /* noop */ }
    })();
     
  }, [isPropertyManager, user?.user_id, lockedPropertyId]);

  useEffect(() => {
    if (!selectedPropertyId) return;
    loadPropertyQR(selectedPropertyId);
    loadUnitQRs(selectedPropertyId);
    loadResidentsCount(selectedPropertyId);
     
  }, [selectedPropertyId]);

  const loadResidentsCount = async (propId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/qr/residents/${propId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setResidentsCount(data.total || 0);
      }
    } catch (_e) { /* noop */ }
  };

  const handleResidentsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPropertyId) return;
    setResidentsUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = String(reader.result || '');
        const b64 = dataUrl.split(',', 2)[1] || '';
        // 1) Parse the file on the server
        const parseRes = await fetch(`${BACKEND_URL}/api/qr/residents/parse`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_b64: b64 }),
        });
        const parseData = await parseRes.json();
        if (!parseRes.ok || !parseData.residents?.length) {
          toast.error(parseData.detail || 'Could not read the file — expected CSV/XLSX with Name + Unit columns');
          setResidentsUploading(false);
          return;
        }
        // 2) Upload the parsed rows
        const upRes = await fetch(`${BACKEND_URL}/api/qr/residents/${selectedPropertyId}/upload`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ residents: parseData.residents }),
        });
        const upData = await upRes.json();
        if (!upRes.ok) {
          toast.error(upData.detail || 'Upload failed');
          setResidentsUploading(false);
          return;
        }
        toast.success(upData.message || `Uploaded ${parseData.residents.length} residents`);
        setResidentsUploading(false);
        // 3) Refresh QR grid + count
        loadUnitQRs(selectedPropertyId);
        loadResidentsCount(selectedPropertyId);
        // Reset the file input so the same file can be reselected later
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    } catch (_e) {
      toast.error('Failed to upload residents file');
      setResidentsUploading(false);
    }
  };

  const loadPropertyQR = async (propId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/qr/property/${propId}`, { credentials: 'include' });
      if (res.ok) {
        setPropertyQR(await res.json());
      } else {
        setPropertyQR(null);
        const data = await res.json().catch(() => ({}));
        if (res.status === 403) toast.error('You do not have access to this property.');
        else if (res.status === 404) toast.error('Property not found.');
        else if (data.detail) toast.error(data.detail);
      }
    } catch (_e) { setPropertyQR(null); }
  };

  const loadUnitQRs = async (propId) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/qr/units/${propId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUnitQRs(data.qr_codes || []);
      }
    } catch (_e) { /* noop */ }
    setLoading(false);
  };

  const batchGenerate = async () => {
    const units = batchInput
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!selectedPropertyId || units.length === 0) {
      toast.error('Enter at least one unit number');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/qr/unit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: selectedPropertyId, unit_numbers: units })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to generate QRs');
        return;
      }
      // Merge with existing list, dedupe by unit_number
      const merged = [...(data.qr_codes || []), ...unitQRs];
      const seen = new Set();
      const deduped = merged.filter((q) => {
        if (seen.has(q.unit_number)) return false;
        seen.add(q.unit_number);
        return true;
      });
      deduped.sort((a, b) => String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true }));
      setUnitQRs(deduped);
      setBatchInput('');
      toast.success(`Generated ${data.qr_codes?.length || 0} QR code(s)`);
    } catch (_e) {
      toast.error('Failed to generate QRs');
    }
  };

  const downloadPNG = (pngBase64, filename) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${pngBase64}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Escape any value before interpolating into the print template.
  // Defends against XSS when unit_number / property_name come from user-controlled DB fields.
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[ch]));
  // Only allow standard base64 characters for the embedded PNG payload.
  const safeB64 = (s) => String(s ?? '').replace(/[^A-Za-z0-9+/=]/g, '');

  const printAll = () => {
    if (unitQRs.length === 0) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const propertyName = escapeHtml(propertyQR?.property_name || '');
    const rows = unitQRs.map((q) => `
            <div class="item">
              <img src="data:image/png;base64,${safeB64(q.qr_png_base64)}" alt="Unit ${escapeHtml(q.unit_number)}" />
              <div class="unit">Unit ${escapeHtml(q.unit_number)}</div>
              <div class="prop">${escapeHtml(q.property_name || '')}</div>
            </div>
          `).join('');
    const html = `
      <html><head><title>NECS Unit QR Codes</title>
      <style>
        body { font-family: sans-serif; background: #fff; padding: 12mm; }
        .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8mm; }
        .item { text-align: center; page-break-inside: avoid; border: 1px dashed #999; padding: 6mm 2mm; border-radius: 4mm; }
        .item img { width: 90%; height: auto; }
        .item .unit { font-weight: 700; font-size: 14pt; margin-top: 2mm; }
        .item .prop { font-size: 9pt; color: #555; }
        h1 { font-family: 'Times New Roman', serif; font-size: 18pt; text-align: center; letter-spacing: 0.1em; }
        .sub { text-align: center; color: #666; margin-bottom: 6mm; font-size: 10pt; }
      </style></head>
      <body>
        <h1>Noble Empire Cleaning Services</h1>
        <div class="sub">Unit QR Codes — ${propertyName}</div>
        <div class="grid">${rows}</div>
      </body></html>`;
    // Use DOM API instead of document.write — write() is the XSS-prone surface
    const doc = w.document;
    doc.open();
    doc.write('<!doctype html>');
    doc.close();
    // Replace the document root via DOMParser so the print payload is parsed, not executed
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    doc.replaceChild(doc.adoptNode(parsed.documentElement), doc.documentElement);
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      {/* ── QR pickup adoption panel — pilot ROI signal ─────────────
          Shows total scans, scan-through rate, and top-3 property
          leaderboard for the selected window. Empty state guides
          the reader to the P1 pilot workflow if no scans yet. */}
      {adoption && (
        <Card
          className="bg-gradient-to-br from-emerald-950/50 via-zinc-900 to-zinc-900 border-2 border-emerald-500/40 p-5"
          data-testid="qr-adoption-panel"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                <h3
                  className="text-lg font-bold text-white"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  QR PICKUP ADOPTION · FIRST-WEEK PANEL
                </h3>
              </div>
              <p className="text-[11px] text-zinc-400 max-w-2xl">
                Scan-through rate for the last {adoption.window_days} day{adoption.window_days === 1 ? '' : 's'}.
                Print stickers via the property + unit QRs below and watch the rate climb as R07 (Dallas · Central) validates the pipeline before scaling.
              </p>
            </div>
            <select
              value={adoptionWindow}
              onChange={(e) => setAdoptionWindow(Number(e.target.value))}
              className="h-8 rounded bg-zinc-950 border border-zinc-700 text-white text-[11px] px-2 focus:outline-none focus:border-emerald-500"
              data-testid="qr-adoption-window"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdoptionKpi
              icon={<ScanLine className="h-4 w-4 text-emerald-400" />}
              label="QR scans"
              value={adoption.total_qr_scans}
              sub={`of ${adoption.total_pickups} total pickups`}
              testid="qr-adoption-scans"
            />
            <AdoptionKpi
              icon={<TrendingUp className="h-4 w-4 text-emerald-400" />}
              label="Scan-through rate"
              value={`${Math.round((adoption.scan_rate || 0) * 100)}%`}
              sub={adoption.total_pickups ? 'QR vs. all pickups' : 'Awaiting first pickup'}
              testid="qr-adoption-rate"
            />
            <AdoptionKpi
              icon={<QrCode className="h-4 w-4 text-emerald-400" />}
              label="Properties scanning"
              value={`${adoption.properties_with_scans} / ${adoption.properties_total}`}
              sub="≥1 scan in window"
              testid="qr-adoption-properties"
            />
            <AdoptionKpi
              icon={<Users className="h-4 w-4 text-emerald-400" />}
              label="Unique units scanned"
              value={(adoption.properties || []).reduce((a, r) => a + (r.unit_scans || 0), 0)}
              sub="Distinct tenant unit QRs"
              testid="qr-adoption-units"
            />
          </div>
          {(adoption.leaderboard || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-emerald-500/20">
              <div className="text-[9px] uppercase tracking-widest text-emerald-400 font-bold mb-1.5">
                Top 3 · scan-through rate
              </div>
              <ol className="space-y-1" data-testid="qr-adoption-leaderboard">
                {adoption.leaderboard.map((row, idx) => (
                  <li key={row.property_id} className="flex items-center justify-between text-[12px] text-zinc-200">
                    <span className="flex items-center gap-2">
                      <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-mono font-bold ${idx === 0 ? 'bg-[#FFD60A] text-black' : idx === 1 ? 'bg-zinc-400 text-black' : 'bg-orange-600 text-white'}`}>
                        {idx + 1}
                      </span>
                      <span className="truncate max-w-[240px]">{row.property_name}</span>
                    </span>
                    <span className="font-mono text-emerald-300">
                      {Math.round(row.scan_rate * 100)}% · {row.qr_scans} scan{row.qr_scans === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {adoption.total_qr_scans === 0 && (
            <p
              className="mt-3 text-[11px] text-zinc-500 italic"
              data-testid="qr-adoption-empty"
            >
              No QR scans yet. Kick off the pilot: pick a Dallas · Central (R07) property → print its site &amp; unit stickers below → task the Site Agent to scan on first pickup.
            </p>
          )}
        </Card>
      )}

      <Card className="bg-zinc-900 border-zinc-700 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <QrCode className="h-5 w-5 text-[#C9A962]" /> QR Codes
            </h3>
            <p className="text-sm text-zinc-400">Property clock-in QR + per-unit pickup QR codes.</p>
          </div>
          <div className="min-w-[220px]" hidden={!!lockedPropertyId}>
            <Label className="text-zinc-400 text-xs">Property</Label>
            <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="qr-property-select">
                <SelectValue placeholder="Choose property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Property (site) QR */}
      <Card className="bg-zinc-900 border-[#C9A962]/40 p-6">
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <h4 className="text-white font-semibold mb-1">Property Clock-In QR</h4>
            <p className="text-xs text-zinc-400 mb-3">Post this at the property entrance. Agents scan to clock in.</p>
            {propertyQR ? (
              <>
                <p className="text-sm text-[#C9A962] font-mono break-all" data-testid="property-qr-payload">
                  {propertyQR.qr_payload}
                </p>
                <Button
                  className="mt-3 font-bold"
                  style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                  onClick={() => downloadPNG(
                    propertyQR.qr_png_base64,
                    `NECS-Property-${propertyQR.property_name?.replace(/\s+/g, '_') || 'QR'}.png`
                  )}
                  data-testid="property-qr-download"
                >
                  <Download className="h-4 w-4 mr-2" /> Download PNG
                </Button>
              </>
            ) : (
              <p className="text-sm text-zinc-500">Select a property to view its QR.</p>
            )}
          </div>
          {propertyQR && (
            <div className="ml-auto">
              <img
                src={`data:image/png;base64,${propertyQR.qr_png_base64}`}
                alt="Property QR"
                className="w-40 h-40 bg-[#2D2D2D] rounded-lg border border-[#C9A962]"
                data-testid="property-qr-image"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Batch generator */}
      <Card className="bg-zinc-900 border-zinc-700 p-6">
        <h4 className="text-white font-semibold mb-1">Generate Unit QR Codes</h4>
        <p className="text-xs text-zinc-400 mb-3">
          Enter unit numbers separated by comma or new line (e.g. 101, 102, 201, 202).
        </p>
        <div className="flex gap-3 flex-wrap">
          <Input
            value={batchInput}
            onChange={(e) => setBatchInput(e.target.value)}
            placeholder="101, 102, 103..."
            className="flex-1 min-w-[260px] bg-zinc-800 border-zinc-700 text-white"
            data-testid="qr-batch-input"
          />
          <Button
            onClick={batchGenerate}
            disabled={!selectedPropertyId}
            className="font-bold"
            style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
            data-testid="qr-batch-generate"
          >
            <Plus className="h-4 w-4 mr-2" /> Generate
          </Button>
        </div>
      </Card>

      {/* Residents roster uploader */}
      <Card className="bg-zinc-900 border-zinc-700 p-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <div>
            <h4 className="text-white font-semibold flex items-center gap-2">
              <Users className="h-4 w-4 text-[#C9A962]" /> Residents Info List
            </h4>
            <p className="text-xs text-zinc-400">
              Upload a CSV or XLSX with columns <strong>Name · Unit Number · Phone · Email</strong>. Each unit QR will show the resident&apos;s name so notifications land accurately.
            </p>
          </div>
          <div>
            <input
              id="residents-upload-input"
              type="file"
              accept=".csv,.xls,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleResidentsUpload}
              className="hidden"
              data-testid="qr-residents-upload-input"
            />
            <Button
              onClick={() => document.getElementById('residents-upload-input')?.click()}
              disabled={!selectedPropertyId || residentsUploading}
              className="font-bold"
              style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
              data-testid="qr-residents-upload-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {residentsUploading ? 'Uploading…' : 'Upload Residents List'}
            </Button>
          </div>
        </div>
        {residentsCount > 0 && (
          <p className="text-xs text-emerald-400 mt-1" data-testid="qr-residents-count">
            ✓ {residentsCount} resident(s) on file for this property
          </p>
        )}
      </Card>

      {/* Unit QR grid */}
      <Card className="bg-zinc-900 border-zinc-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-white font-semibold">Unit QR Codes ({unitQRs.length})</h4>
            <p className="text-xs text-zinc-400">
              Auto-populated from registered tenants + uploaded residents roster + any you generate manually.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={printAll}
            disabled={unitQRs.length === 0}
            className="border-[#C9A962] text-[#C9A962]"
            data-testid="qr-print-all"
          >
            <Printer className="h-4 w-4 mr-2" /> Print sheet
          </Button>
        </div>

        {loading ? (
          <p className="text-zinc-400 text-sm">Loading…</p>
        ) : unitQRs.length === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="qr-units-empty">
            No unit QR codes yet. Upload the residents list above, generate manually, or register tenants with unit numbers.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="qr-units-grid">
            {unitQRs.map((q) => (
              <div
                key={`${q.property_id}-${q.unit_number}`}
                className="border border-zinc-700 rounded-lg p-3 bg-[#2D2D2D] flex flex-col items-center"
                data-testid={`qr-unit-card-${q.unit_number}`}
              >
                <img
                  src={`data:image/png;base64,${q.qr_png_base64}`}
                  alt={`Unit ${q.unit_number}`}
                  className="w-28 h-28 object-contain"
                />
                <p className="mt-2 text-white font-bold">Unit {q.unit_number}</p>
                {q.tenant_name ? (
                  <>
                    <p
                      className="text-[12px] text-[#C9A962] font-semibold text-center"
                      data-testid={`qr-unit-tenant-${q.unit_number}`}
                    >
                      {q.tenant_name}
                    </p>
                    {q.tenant_phone && (
                      <p className="text-[10px] text-zinc-400 truncate max-w-[140px]">{q.tenant_phone}</p>
                    )}
                  </>
                ) : (
                  <p className="text-[10px] italic text-zinc-500">No resident on file</p>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-2 text-[#C9A962] hover:text-[#C9A962]/80 text-xs"
                  onClick={() => downloadPNG(q.qr_png_base64, `NECS-Unit-${q.unit_number}.png`)}
                  data-testid={`qr-unit-download-${q.unit_number}`}
                >
                  <Download className="h-3 w-3 mr-1" /> PNG
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// Compact KPI tile used inside the QR pickup adoption panel.
function AdoptionKpi({ icon, label, value, sub, testid }) {
  return (
    <div
      className="bg-zinc-950 border border-emerald-500/20 rounded-lg p-3"
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-emerald-400 font-bold">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold text-white mt-1 font-mono">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
